

import React, { useState } from 'react';
import { 
  Warehouse, 
  TrendingUp, 
  Shield, 
  Zap, 
  Users, 
  DollarSign, 
  BarChart3, 
  CheckCircle, 
  ArrowRight, 
  Eye, 
  Clock, 
  Target, 
  Settings,
  Cloud,
  Layers,  // Changed from Link to Layers
  Star,
  Play
} from 'lucide-react';

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import InforAlliancePartnerLogo from '../../../../../Assets/Logo/InforAlliancePartner.png'
import Intro from './IntroCarousel';
import WhyChooseus from './WhyChoose';
import WMSBenifits from './Benifits';
import WMSServices from './WMSServices';
import IntroHero from '../../../ResuableComponents/IntroHero';
import ImageSectionReusable from '../../../ResuableComponents/ImageSectionResuable';
import WMSInforgraphicImage from '../../../../../Assets/wms_infographic.png'
import WMSIntroImage from '../../../../../Assets/wms_intro.jpg'
import CTA from './CTA';
import WMSOverview from './WMSOverview';
import WMSIndustries from './Industries';
import WMSFAQ from './FAQ';


const InforWMSComponent = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [hoveredBenefit, setHoveredBenefit] = useState(null);

  const features = [
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Complete Inventory Visibility",
      description: "Gain real-time insight into inventory across all warehouse locations, reducing stock discrepancies, improving accuracy, and ensuring smoother operations.",
      color: "#0ea5e9"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Accelerated Fulfillment Processes",
      description: "Automate and streamline key warehouse activities like picking, packing, and shipping to ensure faster, more accurate deliveries.",
      color: "#8b5cf6"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Built for Business Growth",
      description: "Scale effortlessly as your business expands—Infor WMS supports new product lines, warehouses, and customer requirements without disruption.",
      color: "#10b981"
    },
    {
      icon: <Layers className="w-8 h-8" />,  // Changed from Link to Layers
      title: "Seamless ERP Integration",
      description: "Infor WMS connects natively with Infor ERP systems like LN, M3, and CloudSuite, enabling end-to-end visibility across your entire supply chain.",
      color: "#f59e0b"
    }
  ];

  const benefits = [
    { icon: <Eye className="w-6 h-6" />, title: "Real-Time Inventory Visibility", desc: "Know what you have and where it is — always.", color: "#0ea5e9" },
    { icon: <Clock className="w-6 h-6" />, title: "Faster Order Fulfillment", desc: "Speed up picking, packing, and shipping with optimized workflows.", color: "#8b5cf6" },
    { icon: <Target className="w-6 h-6" />, title: "Improved Accuracy", desc: "Reduce inventory errors, shrinkage, and stock-outs.", color: "#10b981" },
    { icon: <Users className="w-6 h-6" />, title: "Labor Optimization", desc: "Boost workforce productivity with smart task allocation and performance tracking.", color: "#f59e0b" },
    { icon: <Cloud className="w-6 h-6" />, title: "Cloud Flexibility", desc: "Access your warehouse operations anytime, anywhere.", color: "#0ea5e9" },
    { icon: <DollarSign className="w-6 h-6" />, title: "Lower Operational Costs", desc: "Automate manual processes and minimize overhead.", color: "#8b5cf6" }
  ];

  const businessHelps = [
    {
      title: "Boosts Operational Efficiency",
      description: "Automation of warehouse tasks reduces manual errors and speeds up order processing with real-time tracking.",
      icon: <Settings className="w-6 h-6" />,
      bgColor: "#e0f2fe",
      textColor: "#0369a1"
    },
    {
      title: "Improves Inventory Accuracy",
      description: "Tracks inventory down to the location, batch, lot, or serial number, eliminating guesswork.",
      icon: <Target className="w-6 h-6" />,
      bgColor: "#f3e8ff",
      textColor: "#7c3aed"
    },
    {
      title: "Enhances Customer Satisfaction",
      description: "Accurate and faster order fulfillment means fewer delays, errors, and returns.",
      icon: <Star className="w-6 h-6" />,
      bgColor: "#d1fae5",
      textColor: "#059669"
    }
  ];
  return (
    <>
    <Helmet>
    {/* Primary Meta Tags */}
            <title>PTKCS Tech | InforWarehouse – Smart Warehouse & Inventory Management Solutions</title>
            <meta 
              name="description" 
              content="PTKCS Tech provides comprehensive InforWarehouse solutions—including warehouse management, inventory control, system integration, automation, and support—to help businesses optimize operations, reduce costs, and improve accuracy across supply chains." 
            />
            <meta 
              name="keywords" 
              content="InforWarehouse, warehouse management, inventory system, warehouse solutions, WMS integration, Infor warehouse, inventory control, warehouse automation, supply chain optimization, PTKCS Tech, Infor WMS services." 
            />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content="PTKCS Tech | InforWarehouse – Smart Warehouse & Inventory Management Solutions" />
            <meta 
              property="og:description" 
              content="PTKCS Tech provides comprehensive InforWarehouse solutions—including warehouse management, inventory control, system integration, automation, and support—to help businesses optimize operations, reduce costs, and improve accuracy across supply chains." 
            />
            <meta property="og:image" content="https://www.ptkcs.com/static/media/wms_infographic.54cc493ca453cb79fa04.png" />
            
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="PTKCS Tech | InforWarehouse – Smart Warehouse & Inventory Management Solutions" />
            <meta 
              name="twitter:description" 
              content="PTKCS Tech provides comprehensive InforWarehouse solutions—including warehouse management, inventory control, system integration, automation, and support—to help businesses optimize operations, reduce costs, and improve accuracy across supply chains." 
            />
            <meta name="twitter:image" content="https://www.ptkcs.com/static/media/wms_infographic.54cc493ca453cb79fa04.png" />
            
            {/* Additional SEO Tags */}
            <meta name="robots" content="index, follow" />
            <meta name="language" content="English" />
            <meta name="author" content="PTKCS Tech" />
            <link rel="canonical" href={window.location.href} />
      </Helmet>

    <div className="min-h-screen font-satoshi" style={{ backgroundColor: '#FFFFFF' }}>
            <Intro/>
      <WMSOverview/>
      <WMSServices/>
      <WMSIndustries/>
      <WhyChooseus/>
      <WMSFAQ/>

      {/* CTA Section */}
      <CTA/>
    </div>
    </>
  );
};

export default InforWMSComponent;
